import { useEffect, useMemo } from "react";
import { useThumbnailCache } from "hooks/useThumbnailCache";
import { rtctApi } from "services/rtctApi";
import { flipTransform, localText, readResults, resultParts } from "utils/overpasses";
import { useStableState } from "utils/stableState";

// What each scheduled capture brought back (GET devices/<mac>/overpasses/results/), under the satellite passes: the
// newest SHOWN of the last 14 days, each with its photo pair (thumbnails upright by the cameras' saved flips), GNSS
// fix, burst and upload, and the satellite when it sat at a pass. A one-off with no reading yet is waiting, then
// missed. Asked for every 10 minutes while the page is visible.
const POLL_MS = 10 * 60 * 1000;
const SHOWN = 6;

export default function PacketResults({ mac }) {
  const [state, setState] = useStableState({ mac: "", data: null, cameras: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.overpassResults !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      const calibration =
        typeof rtctApi.calibration === "function"
          ? Promise.resolve(rtctApi.calibration(mac, signal)).catch(() => null)
          : Promise.resolve(null);
      Promise.all([Promise.resolve(rtctApi.overpassResults(mac, signal)), calibration])
        .then(([data, cal]) => {
          if (signal.aborted) return;
          const cameras = cal?.cameras && typeof cal.cameras === "object" ? cal.cameras : null;
          setState({ mac, data: readResults(data), cameras, error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "Capture results unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const mine = state.mac === mac;
  const data = mine ? state.data : null;
  const shown = useMemo(() => (data?.results || []).slice(0, SHOWN), [data]);
  const images = useMemo(() => shown.flatMap((r) => (r.photo?.frames || []).filter((f) => !f.expired)), [shown]);
  const urls = useThumbnailCache(images);
  if (!mine || (!data && !state.error)) return null;
  return (
    <div className="field-passes-results" aria-label="Scheduled captures">
      <h3>Scheduled captures, last {data?.days || 14} days</h3>
      {state.error && <div className="field-alert">{state.error}</div>}
      {data && shown.length === 0 && (
        <p className="field-caption">None yet. A ground-truth packet shows here after its pass.</p>
      )}
      {shown.length > 0 && (
        <ul>
          {shown.map((r) => (
            <li key={r.t} className={`is-${r.status}`}>
              <b>{localText(r.local)}</b>
              {r.pass ? ` · ${r.pass.satellite}` : ""}
              {" · "}
              {resultParts(r).join(" · ")}
              {(r.photo?.frames || []).some((f) => urls[f.id]) && (
                <span className="field-passes-thumbs">
                  {r.photo.frames
                    .filter((f) => urls[f.id])
                    .map((f) => (
                      <img
                        key={f.id}
                        src={urls[f.id]}
                        alt={`Camera ${f.camera}`}
                        style={flipTransform(state.cameras?.[f.camera]) ? { transform: flipTransform(state.cameras[f.camera]) } : undefined}
                      />
                    ))}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
